import type { InsuranceOption } from "./types";
import type { InsuranceOptionGroup } from "../insurance-option-groups/types";

/**
 * Selection rules for grouped insurance.
 *
 * The admin board decides the shape; this decides the behaviour. Both the
 * renter-facing selector and any caller that needs to validate a selection
 * before submitting go through here, so "choose one" means the same thing
 * in the UI and at the point of confirm.
 *
 * These are UI rules, not the authority. The server validates the same
 * constraints independently, because anything that only lives in the
 * browser can be bypassed by posting the ids directly.
 */

export interface GroupedOptions {
  group: InsuranceOptionGroup | null;
  options: InsuranceOption[];
}

const bySortOrder = <T extends { sort_order: number }>(a: T, b: T) =>
  a.sort_order - b.sort_order;

/**
 * Buckets options under their group, ungrouped last.
 *
 * With no groups configured every option is ungrouped, which is exactly the
 * flat checkbox list that existed before groups, so callers that never pass
 * groups keep their current behaviour.
 */
export function groupOptions(
  options: InsuranceOption[],
  groups: InsuranceOptionGroup[] = [],
): GroupedOptions[] {
  const visibleGroups = [...groups].filter((g) => g.active).sort(bySortOrder);

  const sortedOptions = [...options].sort(bySortOrder);
  const known = new Set(visibleGroups.map((g) => g.id));

  const buckets: GroupedOptions[] = visibleGroups
    .map((group) => ({
      group,
      options: sortedOptions.filter((o) => o.group_id === group.id),
    }))
    .filter((bucket) => bucket.options.length > 0);

  const ungrouped = sortedOptions.filter(
    (o) => !o.group_id || !known.has(o.group_id),
  );

  if (ungrouped.length > 0) {
    buckets.push({ group: null, options: ungrouped });
  }

  return buckets;
}

export function selectionModeFor(
  option: InsuranceOption,
  groups: InsuranceOptionGroup[] = [],
): "single" | "multiple" {
  const group = groups.find((g) => g.id === option.group_id);
  return group?.selection_mode === "single" ? "single" : "multiple";
}

/**
 * Applies one click and returns the next selection.
 *
 * Inside a choose-one group this behaves like a real radio: picking a tier
 * clears its siblings, and clicking the tier that is already selected does
 * not clear it when the group is required — a radio you can empty by
 * clicking twice is a well-known way to lose a required answer by accident.
 */
export function toggleInsuranceSelection(
  optionId: string,
  selectedIds: string[],
  options: InsuranceOption[],
  groups: InsuranceOptionGroup[] = [],
): string[] {
  const option = options.find((o) => o.id === optionId);
  if (!option) return selectedIds;

  const isSelected = selectedIds.includes(optionId);
  const group = groups.find((g) => g.id === option.group_id);

  if (group?.selection_mode === "single") {
    const siblingIds = new Set(
      options.filter((o) => o.group_id === group.id).map((o) => o.id),
    );

    if (isSelected) {
      // Required groups keep their answer; optional ones can be cleared.
      if (group.required) return selectedIds;
      return selectedIds.filter((id) => id !== optionId);
    }

    return [...selectedIds.filter((id) => !siblingIds.has(id)), optionId];
  }

  // Ungrouped and choose-any: an option flagged Required is not the
  // renter's to turn off.
  if (option.required)
    return isSelected ? selectedIds : [...selectedIds, optionId];

  return isSelected
    ? selectedIds.filter((id) => id !== optionId)
    : [...selectedIds, optionId];
}

/**
 * Options the renter never gets to decide, pre-applied.
 *
 * Per-option `required` is honoured only outside choose-one groups. Inside
 * one it cannot be satisfied without defeating the choice, so it is ignored
 * here and surfaced to the admin as a configuration error instead.
 */
export function autoSelectedIds(
  selectedIds: string[],
  options: InsuranceOption[],
  groups: InsuranceOptionGroup[] = [],
): string[] {
  const forced = options
    .filter((o) => o.required && o.active)
    .filter((o) => selectionModeFor(o, groups) === "multiple")
    .map((o) => o.id);

  const missing = forced.filter((id) => !selectedIds.includes(id));
  return missing.length === 0 ? selectedIds : [...selectedIds, ...missing];
}

export interface UnresolvedGroup {
  group: InsuranceOptionGroup;
  message: string;
}

/**
 * Required groups the renter has not answered yet. Callers use this to
 * block confirm and to point at the group that still needs a decision.
 */
export function unresolvedRequiredGroups(
  selectedIds: string[],
  options: InsuranceOption[],
  groups: InsuranceOptionGroup[] = [],
): UnresolvedGroup[] {
  const selected = new Set(selectedIds);

  return groups
    .filter((group) => group.active && group.required)
    .filter((group) => {
      const groupOptionIds = options
        .filter((o) => o.group_id === group.id && o.active)
        .map((o) => o.id);

      // A required group with nothing to pick cannot be answered and must
      // not be allowed to deadlock checkout.
      if (groupOptionIds.length === 0) return false;

      return !groupOptionIds.some((id) => selected.has(id));
    })
    .map((group) => ({
      group,
      message:
        group.selection_mode === "single"
          ? `Choose an option for ${group.name}.`
          : `Choose at least one option for ${group.name}.`,
    }));
}
